import { AxiosRequestConfig, AxiosResponse } from 'axios'

import {
  OAuthApi,
  UserApi,
  OrganizationApi,
  ClusterApi,
  ReportApi,
  HealthReportApi,
  ApiKeyApi,
  GlobalDashboardApi,
  LoginValidation,
  OrgInvite,
  OrgUpdateOrganizationReq,
  CommonRebuild,
  ClusterUpdateClusterReq,
  OrgTaskStatus,
  ClusterDataSummaryResponse,
  OrgRole,
  OldreportTouchComparisonReportReq,
  ReportsApi,
  ReportTouchReportRequest,
  ClusterSloganApi,
  DefaultApi,
  MetricApi,
  OldreportTouchTrafficComparisonReportReq,
  ResourcePoolApi
} from '@clinic/client'
import { getRegionRes } from '~/utils/region-res'

import { axiosInstance } from './base'
import { GrafanaQueryRangeParams } from './prom'

////////////

function getApiBasePath(): string {
  return `/clinic/api/v1`
}
const basePath = getApiBasePath()

// TODO: remove xCsrfToken parameter from all api methods
// instead, we add it to the axios instance header by axios request interceptor
function getCsrfToken() {
  return ''
}

////////////
// oauth
const oauthApi = new OAuthApi(undefined, basePath, axiosInstance)

export function getAuthUrl(
  name: 'internal' | 'ask_tug' | 'tidbcloud' | 'pingkai',
  redirectUri: string
) {
  return oauthApi.loginOauthNameInfoGet(name, redirectUri)
}

export function validateOAuth(
  name: 'internal' | 'ask_tug' | 'tidbcloud' | 'pingkai' | 'pingkaicloud',
  validation: LoginValidation
) {
  return oauthApi.loginOauthNameValidatePost(name as any, validation)
}

export function logout() {
  return oauthApi.loginLogoutPost()
}

////////////
// user

const userApi = new UserApi(undefined, basePath, axiosInstance)

export function getUserProfile() {
  return userApi.userProfileGet(getCsrfToken())
}

////////////
// org

const orgApi = new OrganizationApi(undefined, basePath, axiosInstance)

export function listOrgs(
  name?: string,
  filter?: string,
  limit?: number,
  page?: number
) {
  return orgApi.orgsGet(getCsrfToken(), name, filter, limit, page)
}

export function getOrgInfo(oid: string) {
  return orgApi.orgsOidGet(getCsrfToken(), oid)
}

export function createOrg(orgName: string) {
  return orgApi.orgsPost(getCsrfToken(), { name: orgName })
}

export function updateOrg({
  oid,
  org
}: {
  oid: string
  org: OrgUpdateOrganizationReq
}) {
  return orgApi.orgsOidPut(getCsrfToken(), oid, org)
}

export function delOrg(oid: string) {
  return orgApi.orgsOidDelete(getCsrfToken(), oid)
}

export function starOrg(oid: string) {
  return orgApi.orgsOidStarPost(getCsrfToken(), oid)
}

export function unStarOrg(oid: string) {
  return orgApi.orgsOidStarDelete(getCsrfToken(), oid)
}

export function orgUsers(oid: string) {
  return orgApi.orgsOidUsersGet(getCsrfToken(), oid)
}

export function inviteUser(oid: string, invites: OrgInvite[]) {
  return orgApi.orgsOidUsersInvitePost(getCsrfToken(), oid, { invites })
}

export function editUserRole({
  oid,
  userId,
  orgRole
}: {
  oid: string
  userId: number
  orgRole: OrgRole
}) {
  return orgApi.orgsOidUsersIdPut(getCsrfToken(), oid, userId, orgRole)
}

export function delUser({ oid, userId }: { oid: string; userId: number }) {
  return orgApi.orgsOidUsersIdDelete(getCsrfToken(), oid, userId)
}

export function listRoles(oid: string) {
  return orgApi.orgsOidRolesGet(getCsrfToken(), oid)
}

export function listTasks(oid: string): Promise<{ data: OrgTaskStatus[] }> {
  return orgApi.orgsOidTasksGet(getCsrfToken(), oid)
}

export function getOrgIdFromExternalId(eid: string) {
  return orgApi.orgsExternalEidGet(getCsrfToken(), eid)
}

export function listOrgProjects(oid: string) {
  return orgApi.orgsOidProjectsGet(getCsrfToken(), oid)
}

/////////////
// cluster

export const clusterApi = new ClusterApi(undefined, basePath, axiosInstance)

export function listOrgClusters(
  oid: string,
  query?: string,
  limit?: number,
  page?: number,
  projectId?: string,
  showDeleted?: boolean
) {
  return clusterApi.orgsOidClustersGet(
    getCsrfToken(),
    oid,
    limit,
    query,
    page,
    projectId,
    showDeleted
  )
}

export function getClusterInfo(
  oid: string,
  cid: string,
  config?: AxiosRequestConfig
) {
  return clusterApi.orgsOidClustersCidGet(getCsrfToken(), oid, cid, config)
}

/** Lightweight cluster lookup by id only (GET /clusters/{cid}). */
export function getClusterById(cid: string) {
  return clusterApi.clustersCidGet(getCsrfToken(), '', cid)
}

function readStringPropertyFromRawJson(
  rawJson: string,
  property: string
): string | undefined {
  const match = rawJson.match(
    new RegExp(`"${property}"\\s*:\\s*(?:"([^"]+)"|(-?\\d+))`)
  )
  return match?.[1] ?? match?.[2]
}

export function updateClusterInfo({
  oid,
  cid,
  req
}: {
  oid: string
  cid: string
  req: ClusterUpdateClusterReq
}) {
  return clusterApi.orgsOidClustersCidPut(getCsrfToken(), oid, cid, req)
}

export function delCluster({ oid, cid }: { oid: string; cid: string }) {
  return clusterApi.orgsOidClustersCidDelete(getCsrfToken(), oid, cid)
}

export function getClusterMetricsOverview(orgId: string, cid: string) {
  return clusterApi.orgsOidClustersCidMetricsGet(getCsrfToken(), orgId, cid)
}

export function getClusterDataInfo(
  oid: string,
  cid: string,
  startTime?: number,
  endTime?: number
): Promise<AxiosResponse<ClusterDataSummaryResponse, any>> {
  if (startTime && endTime) {
    return clusterApi.orgsOidClustersCidDataGet(
      getCsrfToken(),
      oid,
      cid,
      startTime,
      endTime
    )
  }
  // TODO: remove it when swagger doc updates
  return axiosInstance.get(`/clinic/api/v1/orgs/${oid}/clusters/${cid}/data`)
}

export function getClusterDataStatus(
  oid: string,
  cid: string,
  startTime: number,
  endTime: number,
  dataType: number
) {
  return clusterApi.orgsOidClustersCidDataStatusGet(
    getCsrfToken(),
    oid,
    cid,
    startTime,
    endTime,
    dataType as 1 | 2
  )
}

export function rebuildClusterData({
  oid,
  cid,
  rebuild
}: {
  oid: string
  cid: string
  rebuild: CommonRebuild
}) {
  return clusterApi.orgsOidClustersCidRebuildPut(
    getCsrfToken(),
    oid,
    cid,
    rebuild
  )
}

export function getFileSize(oid: string, cid: string, itemId: string) {
  return clusterApi.orgsOidClustersCidItemidMetaGet(
    getCsrfToken(),
    oid,
    cid,
    itemId
  )
}

export function getLogUrl(
  oid: string,
  cid: string,
  itemId: string,
  startTime: number,
  endTime: number
) {
  return clusterApi.orgsOidClustersCidLogurlItemidGet(
    getCsrfToken(),
    oid,
    cid,
    itemId,
    startTime,
    endTime
  )
}

export function getMetricDashboards(
  oid: string,
  cid: string,
  startTime: number,
  endTime: number,
  version?: string
) {
  return clusterApi.orgsOidClustersCidDashboardGet(
    getCsrfToken(),
    oid,
    cid,
    startTime,
    endTime,
    version
  )
}

export function getDashboardVersionsList(oid: string, cid: string) {
  return clusterApi.orgsOidClustersCidDashboardVersionsGet(
    getCsrfToken(),
    oid,
    cid
  )
}

//////
// api key

const apiKeyApi = new ApiKeyApi(undefined, basePath, axiosInstance)

export function listTokens(oid: string) {
  return apiKeyApi.orgsOidKeysGet(getCsrfToken(), oid)
}

export function genUploadToken(oid: string) {
  return apiKeyApi.orgsOidKeysPost(getCsrfToken(), oid)
}

export function delUploadToken(oid: string, kid: number) {
  return apiKeyApi.orgsOidKeysIdDelete(getCsrfToken(), oid, kid)
}

export function triggerModelAnalysis(req: any) {
  const apiEndpoint = getRegionRes().executeAPIPathBase
  return axiosInstance.post(`${apiEndpoint}/task/trigger`, req)
}

// report v2

const reportsApi = new ReportsApi(undefined, basePath, axiosInstance)

export function getReportList(
  oid: string,
  cid: string,
  reportType: string,
  limit?: number,
  page?: number
) {
  return reportsApi.reportsOidCidGet(
    getCsrfToken(),
    oid,
    cid,
    reportType,
    limit,
    page
  )
}

export function getReport(oid: string, cid: string, rid: string) {
  return reportsApi.reportsOidCidRidGet(getCsrfToken(), oid, cid, rid)
}

export function createReport(
  oid: string,
  cid: string,
  reportType: string,
  reportParams: ReportTouchReportRequest
) {
  return reportsApi.reportsOidCidPost(
    getCsrfToken(),
    oid,
    cid,
    reportType,
    reportParams
  )
}

export function deleteReport(oid: string, cid: string, rid: string) {
  return reportsApi.reportsOidCidRidDelete(getCsrfToken(), oid, cid, rid)
}

export function getReportStats(event: string, hash: string = '') {
  return reportsApi.reportsStatisticsGet(getCsrfToken(), event, hash)
}

/////////////
// report
const reportApi = new ReportApi(undefined, basePath, axiosInstance)

/******************
 * Comparison Report
 ******************/

export function getComparisonReportList(
  oid: string,
  cid: string,
  limit?: number,
  page?: number
) {
  return reportApi.computeEngineOidCidComparisonGet(
    getCsrfToken(),
    oid,
    cid,
    limit,
    page
  )
}

export function getComparisonReport(
  oid: string,
  cid: string,
  rid: string,
  brief?: boolean
) {
  return reportApi.computeEngineOidCidComparisonRidGet(
    getCsrfToken(),
    oid,
    cid,
    rid,
    brief
  )
}

export function createComparisonReport(
  oid: string,
  cid: string,
  reportParam: OldreportTouchComparisonReportReq
) {
  return reportApi.computeEngineOidCidComparisonPost(
    getCsrfToken(),
    oid,
    cid,
    reportParam
  )
}

export function deleteComparisonReport(args: {
  oid: string
  cid: string
  rid: string
}) {
  return reportApi.computeEngineOidCidComparisonRidDelete(
    getCsrfToken(),
    args.oid,
    args.cid,
    args.rid
  )
}

/******************
 * Traffic Mirror Report
 ******************/

export function getTrafficMirrorReportList(
  oid: string,
  cid: string,
  limit?: number,
  page?: number
) {
  return reportApi.computeEngineOidCidTrafficcomparisonGet(
    getCsrfToken(),
    oid,
    cid,
    limit,
    page
  )
}

export function getTrafficMirrorReport(
  oid: string,
  cid: string,
  rid: string,
  brief?: boolean
) {
  return reportApi.computeEngineOidCidTrafficcomparisonRidGet(
    getCsrfToken(),
    oid,
    cid,
    rid,
    brief
  )
}

export function createTrafficMirrorReport(
  oid: string,
  cid: string,
  reportParam: OldreportTouchTrafficComparisonReportReq
) {
  return reportApi.computeEngineOidCidTrafficcomparisonPost(
    getCsrfToken(),
    oid,
    cid,
    reportParam
  )
}

export function deleteTrafficMirrorReport(args: {
  oid: string
  cid: string
  rid: string
}) {
  return reportApi.computeEngineOidCidTrafficcomparisonRidDelete(
    getCsrfToken(),
    args.oid,
    args.cid,
    args.rid
  )
}

/******************
 * Health Report
 ******************/

const healthReportApi = new HealthReportApi(undefined, basePath, axiosInstance)

export function getHealthReportList(args: {
  orgId: string
  clusterId: string
  startTime: number
  endTime: number
  brief?: boolean
}) {
  return healthReportApi
    .computeEngineOidCidHealthGet(
      getCsrfToken(),
      args.orgId,
      args.clusterId,
      args.startTime,
      args.endTime,
      args.brief
    )
    .then((response) => response?.data?.reports)
}

export function getHealthReportDetail(args: {
  orgId: string
  clusterId: string
  reportId: string
}) {
  return healthReportApi
    .computeEngineOidCidHealthRidGet(
      getCsrfToken(),
      args.orgId,
      args.clusterId,
      args.reportId
    )
    .then((response) => response?.data)
}

export function createHealthReport(args: {
  orgId: string
  clusterId: string
  itemId?: string
  startTime?: number
  endTime?: number
}) {
  if (args.itemId) {
    return healthReportApi.computeEngineOidCidHealthPost(
      getCsrfToken(),
      args.orgId,
      args.clusterId,
      { itemID: args.itemId }
    )
  } else {
    return healthReportApi.computeEngineOidCidHealthPost(
      getCsrfToken(),
      args.orgId,
      args.clusterId,
      { startTime: args.startTime, endTime: args.endTime }
    )
  }
}

export function deleteHealthReport(args: {
  oid: string
  cid: string
  rid: string
}) {
  return healthReportApi.computeEngineOidCidHealthRidDelete(
    getCsrfToken(),
    args.oid,
    args.cid,
    args.rid
  )
}

/******************
 * Global Dashboard
 ******************/
const globalDashboardApi = new GlobalDashboardApi(
  undefined,
  basePath,
  axiosInstance
)

export function getGlobalClusters(
  query: string,
  showDeleted: boolean,
  clusterStatus?: Array<string>,
  clusterVersion?: Array<string>,
  tenantPlan?: Array<string>,
  controlPlaneArch?: Array<string>,
  provider?: Array<string>,
  region?: Array<string>,
  deployType?: Array<string>,
  sort?: string,
  order?: string,
  limit?: number,
  page?: number
) {
  return globalDashboardApi.dashboardClustersGet(
    getCsrfToken(),
    query.trim(),
    showDeleted,
    undefined,
    clusterStatus,
    clusterVersion,
    undefined, // deployType
    deployType,
    tenantPlan,
    controlPlaneArch,
    provider,
    region,
    sort,
    order,
    limit,
    page
  )
}

export async function getClusterOrgId(
  clusterId: string
): Promise<string | undefined> {
  const res = await axiosInstance.get<string>(
    `${basePath}/clusters/${encodeURIComponent(clusterId)}`,
    {
      transformResponse: [(data) => data]
    }
  )
  const orgId =
    readStringPropertyFromRawJson(res.data, 'orgId') ??
    (JSON.parse(res.data).orgId as string | number | undefined)
  return orgId !== undefined ? String(orgId) : undefined
}

export function getCloudRegions() {
  return globalDashboardApi.dashboardRegionsGet(getCsrfToken())
}

export function getActiveQPS(
  threshold: number,
  beginTs: number,
  endTs: number,
  region: string[],
  deployType: string[],
  search: string
) {
  return globalDashboardApi.dashboardActiveqpsGet(
    threshold + '',
    beginTs,
    endTs,
    getCsrfToken(),
    search,
    region,
    deployType
  )
}

/******************
 * Cluster Slogan Api
 ******************/
const clusterSloganApi = new ClusterSloganApi(
  undefined,
  basePath,
  axiosInstance
)

export function getClusterSlogan(
  orgId: string,
  clusterId: string,
  limit?: number,
  page?: number
) {
  return clusterSloganApi.orgsOidClustersCidSlogansGet(
    getCsrfToken(),
    orgId,
    clusterId,
    limit,
    page
  )
}

export const clinicAPI = new DefaultApi(undefined, basePath, axiosInstance)

export const getTiflashConfig = (orgId: string, clusterId: string) => {
  return axiosInstance.get(
    `/clinic/api/v1/dashboard/proxy/orgs/${orgId}/clusters/${clusterId}/tiflash/config`,
    {
      headers: {
        'x-cluster-id': clusterId,
        'x-org-id': orgId
      }
    }
  )
}

export const metricApi = new MetricApi(undefined, basePath, axiosInstance)

export const getDataMetrics = ({
  query,
  orgId,
  clusterId,
  start,
  end,
  step,
  isCloud
}: GrafanaQueryRangeParams) => {
  return metricApi.dataMetricsGet(
    isCloud ? 'cloud' : 'op',
    orgId,
    clusterId,
    query,
    start,
    end,
    step.toString()
  )
}

export const resourcePoolApi = new ResourcePoolApi(
  undefined,
  basePath,
  axiosInstance
)

export function getResourcePoolComponents(oid: string, cid: string) {
  return resourcePoolApi.orgsOidClustersCidResourcePoolComponentsGet(
    getCsrfToken(),
    oid,
    cid
  )
}
